import moment from "moment-timezone";
import platformConfig from "../platformConfig";

export const EN_DASH = "–";
export const ANNOUNCEMENT_FEED_PAGE_SIZE = 10;
export const ANNOUNCEMENT_TITLE_MAX_CHARS = 36;
export const UNREAD_DOT_COLOR = "#F04B55";

export const getRichTextPlainText = (html) => {
  const richTextHtml = String(html || "");

  if (typeof DOMParser !== "undefined") {
    const parsedDocument = new DOMParser().parseFromString(richTextHtml, "text/html");
    return parsedDocument.body?.textContent || "";
  }

  return richTextHtml.replace(/&nbsp;/gi, " ").replace(/<[^>]*>/g, " ");
};

export const isEmptyRichText = (html) => getRichTextPlainText(html).replace(/\u00a0/g, " ").trim() === "";

export const isBlankText = (value) => String(value || "").trim() === "";

const ALLOWED_HTML_TAGS = new Set([
  "A",
  "B",
  "BLOCKQUOTE",
  "BR",
  "DIV",
  "EM",
  "H1",
  "H2",
  "H3",
  "I",
  "LI",
  "OL",
  "P",
  "S",
  "SPAN",
  "STRONG",
  "U",
  "UL",
]);

const isSafeHref = (href) => {
  const value = String(href || "").trim();
  if (value === "") {
    return false;
  }
  return /^(https?:|mailto:)/i.test(value);
};

export const sanitizeAnnouncementHtml = (html) => {
  if (typeof DOMParser === "undefined") {
    return String(html || "");
  }

  const parsedDocument = new DOMParser().parseFromString(String(html || ""), "text/html");
  const walk = (node) => {
    const children = Array.from(node.childNodes);
    children.forEach((child) => {
      if (child.nodeType === 1) {
        if (!ALLOWED_HTML_TAGS.has(child.tagName)) {
          const textNode = parsedDocument.createTextNode(child.textContent || "");
          node.replaceChild(textNode, child);
          return;
        }

        Array.from(child.attributes).forEach((attribute) => {
          const name = attribute.name.toLowerCase();
          if (name.startsWith("on") || name === "style") {
            child.removeAttribute(attribute.name);
            return;
          }
          if (child.tagName === "A" && name === "href") {
            if (!isSafeHref(attribute.value)) {
              child.removeAttribute("href");
            }
            return;
          }
          if (!(child.tagName === "A" && (name === "href" || name === "target" || name === "rel"))) {
            child.removeAttribute(attribute.name);
          }
        });

        if (child.tagName === "A") {
          child.setAttribute("target", "_blank");
          child.setAttribute("rel", "noopener noreferrer");
        }

        walk(child);
      } else if (child.nodeType === 8) {
        node.removeChild(child);
      }
    });
  };

  walk(parsedDocument.body);

  const isEmptyBlock = (node) => {
    if (!node || node.nodeType !== 1) {
      return false;
    }
    if (node.tagName !== "P" && node.tagName !== "DIV") {
      return false;
    }
    return (node.textContent || "").replace(/\u00a0/g, " ").trim() === "";
  };

  const body = parsedDocument.body;
  let block = body.firstElementChild;
  while (block) {
    const next = block.nextElementSibling;
    if (isEmptyBlock(block) && next && isEmptyBlock(next)) {
      body.removeChild(block);
    }
    block = next;
  }
  while (body.lastElementChild && isEmptyBlock(body.lastElementChild)) {
    body.removeChild(body.lastElementChild);
  }

  return parsedDocument.body.innerHTML;
};

export const isPublishedFlag = (value) =>
  value === true || value === 1 || String(value).toLowerCase() === "true";

export const ANNOUNCEMENT_AUDIENCE_QUERY_TYPES = ["retailer", "cyreen", "tenant", "brand", "agency"];

export const ANNOUNCEMENT_TYPE_LABELS = {
  retailer: "Retailer",
  cyreen: "Cyreen",
  tenant: "Tenant",
  brand: "Brand",
  agency: "Agency",
};

export const ANNOUNCEMENT_TYPE_OPTIONS = ANNOUNCEMENT_AUDIENCE_QUERY_TYPES.map((type) => ({
  id: type,
  label: ANNOUNCEMENT_TYPE_LABELS[type],
}));

export const normalizeAnnouncementType = (value) => String(value || "").trim().toLowerCase();

export const isAnnouncementAudienceQueryType = (value) =>
  ANNOUNCEMENT_AUDIENCE_QUERY_TYPES.includes(normalizeAnnouncementType(value));

const announcementTypeToken = (value) => {
  if (value && typeof value === "object") {
    return normalizeAnnouncementType(value.id || value.label || value.type);
  }
  return normalizeAnnouncementType(value);
};

export const parseAnnouncementTypes = (value) => {
  let rawItems = [];
  if (Array.isArray(value)) {
    rawItems = value;
  } else if (typeof value === "string") {
    rawItems = value.split(",");
  }

  const unique = [];
  rawItems.forEach((item) => {
    const normalized = announcementTypeToken(item);
    if (normalized && isAnnouncementAudienceQueryType(normalized) && unique.indexOf(normalized) === -1) {
      unique.push(normalized);
    }
  });
  return unique;
};

export const serializeAnnouncementTypes = (value) => {
  const parsed = parseAnnouncementTypes(value);
  return parsed.slice().sort((left, right) => left.localeCompare(right));
};

export const announcementTypeOptionsFromValue = (value) =>
  ANNOUNCEMENT_TYPE_OPTIONS.filter((option) => parseAnnouncementTypes(value).indexOf(option.id) !== -1);

export const formatAnnouncementTypesList = (announcement) => {
  const types = parseAnnouncementTypes(announcement?.types != null ? announcement.types : announcement?.type);
  if (!types.length) {
    return EN_DASH;
  }
  return types.map((type) => ANNOUNCEMENT_TYPE_LABELS[type] || type).join(", ");
};

export const formatAnnouncementTypesDisplay = (announcement) => {
  const types = parseAnnouncementTypes(announcement?.types != null ? announcement.types : announcement?.type);
  if (!types.length) {
    return EN_DASH;
  }
  const hasEveryAudienceType = ANNOUNCEMENT_AUDIENCE_QUERY_TYPES.every((type) => types.indexOf(type) !== -1);
  if (hasEveryAudienceType) {
    return "All";
  }
  return formatAnnouncementTypesList(announcement);
};

export const normalizeAnnouncement = (raw) => {
  const record = raw && typeof raw === "object" ? raw : {};
  const types = parseAnnouncementTypes(record.types != null ? record.types : record.type);

  return {
    id: record.id,
    title: typeof record.title === "string" ? record.title : record.title == null ? "" : String(record.title),
    description: typeof record.description === "string" ? record.description : "",
    types,
    type: serializeAnnouncementTypes(types),
    published: isPublishedFlag(record.published),
    seen: record.seen === true,
    published_timestamp: record.published_timestamp || null,
    published_by: record.published_by || "",
    last_edit_timestamp: record.last_edit_timestamp || null,
    last_edit_by: record.last_edit_by || "",
  };
};

export const extractAnnouncementPage = (response, request = {}) => {
  let list = [];
  let meta = {};

  if (Array.isArray(response)) {
    list = response;
  } else if (response && typeof response === "object") {
    if (Array.isArray(response.content)) {
      list = response.content;
      meta = response;
    } else if (response.content && typeof response.content === "object" && Array.isArray(response.content.content)) {
      list = response.content.content;
      meta = response.content;
    } else if (Array.isArray(response.data)) {
      list = response.data;
      meta = response;
    } else if (response.data && typeof response.data === "object" && Array.isArray(response.data.content)) {
      list = response.data.content;
      meta = response.data;
    } else if (Array.isArray(response.announcements)) {
      list = response.announcements;
      meta = response;
    } else if (Array.isArray(response.items)) {
      list = response.items;
      meta = response;
    }
  }

  const requestedPage = Number(request.page);
  const requestedSize = Number(request.size);
  const number = Number.isFinite(Number(meta.number))
    ? Number(meta.number)
    : Number.isInteger(requestedPage) && requestedPage >= 0
      ? requestedPage
      : 0;
  const size = Number.isFinite(Number(meta.size))
    ? Number(meta.size)
    : Number.isInteger(requestedSize) && requestedSize > 0
      ? requestedSize
      : list.length;
  const totalElements = Number.isFinite(Number(meta.totalElements))
    ? Number(meta.totalElements)
    : list.length;
  const totalPages = Number.isFinite(Number(meta.totalPages))
    ? Number(meta.totalPages)
    : size > 0
      ? Math.ceil(totalElements / size)
      : 0;
  const numberOfElements = Number.isFinite(Number(meta.numberOfElements))
    ? Number(meta.numberOfElements)
    : list.length;
  const requested = Number.isInteger(requestedSize) && requestedSize > 0 ? requestedSize : size;
  const last =
    typeof meta.last === "boolean"
      ? meta.last
      : numberOfElements < requested || (requested > 0 && number * requested + numberOfElements >= totalElements);
  const hasMore = numberOfElements >= requested && last === false;

  return { list, number, size, totalElements, totalPages, numberOfElements, last, hasMore };
};

export const extractAnnouncementList = (response) => extractAnnouncementPage(response).list;

export const sortAnnouncementsByIdDesc = (announcements) =>
  [...announcements].sort((left, right) => Number(right.id) - Number(left.id));

export const sortAnnouncementsByPublishDateDesc = (announcements) =>
  [...announcements].sort((left, right) => {
    const rightTime = new Date(right.published_timestamp || 0).getTime();
    const leftTime = new Date(left.published_timestamp || 0).getTime();
    if (rightTime !== leftTime) {
      return rightTime - leftTime;
    }
    return Number(right.id) - Number(left.id);
  });

export const publishedStatusLabel = (announcement) => (announcement?.published ? "Published" : "Draft");

export const publishedOnDisplay = (announcement, formatDateFn) => {
  if (!announcement?.published || !announcement.published_timestamp) {
    return EN_DASH;
  }
  return formatDateFn(announcement.published_timestamp);
};

export const publishedByDisplay = (announcement) => {
  if (!announcement?.published || !announcement.published_by) {
    return EN_DASH;
  }
  return announcement.published_by;
};

export const lastEditDisplay = (announcement, formatDateFn) => {
  const username = String(announcement?.last_edit_by || "").trim();
  const dateValue = announcement?.last_edit_timestamp
    ? formatDateFn(announcement.last_edit_timestamp)
    : "";
  if (!username && !dateValue) {
    return EN_DASH;
  }
  if (!username) {
    return dateValue;
  }
  if (!dateValue) {
    return username;
  }
  return `${username} ${dateValue}`;
};

export const filterAnnouncementsByAudienceType = (announcements, audienceType) => {
  const requested = normalizeAnnouncementType(audienceType);
  if (!requested || !isAnnouncementAudienceQueryType(requested)) {
    return announcements || [];
  }

  return (announcements || []).filter((item) => {
    const itemTypes = Array.isArray(item?.types) ? item.types : parseAnnouncementTypes(item?.type);
    return itemTypes.indexOf(requested) !== -1;
  });
};

export const normalizeHref = (value) => {
  const trimmed = String(value || "").trim();
  if (trimmed === "") {
    return "";
  }
  if (/^(javascript|data|vbscript):/i.test(trimmed)) {
    return "";
  }
  if (/^(https?:|mailto:)/i.test(trimmed)) {
    return trimmed;
  }
  return `https://${trimmed}`;
};

export const validateAnnouncementForm = ({ title, description, types }) => {
  const errors = {};
  if (isBlankText(title)) {
    errors.title = "Title must not be empty.";
  }
  if (isEmptyRichText(description)) {
    errors.description = "Announcement text must not be empty.";
  }
  if (parseAnnouncementTypes(types).length === 0) {
    errors.types = "Type must not be empty.";
  }
  return {
    error: Object.keys(errors).length > 0,
    errors,
  };
};

export const formatAnnouncementDate = (date) => {
  if (!date) {
    return "";
  }
  return moment.tz(date, platformConfig.timezone).format("MMM D, YYYY");
};

export const truncateAnnouncementTitle = (text, maxChars = ANNOUNCEMENT_TITLE_MAX_CHARS) => {
  const full = String(text || "");
  if (full.length <= maxChars) {
    return { display: full, truncated: false, full };
  }
  return {
    display: `${full.slice(0, maxChars)}...`,
    truncated: true,
    full,
  };
};
